/**
 * SlickGrid Sort Utility Functions
 *
 * Pure sort functions that can be tested independently without DOM dependencies.
 * These handle all data types correctly including null values, strings, numbers, and dates.
 */

import dayjs from "dayjs";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type SortDirection = "asc" | "desc";

export interface SortCondition {
  column: string;
  direction: SortDirection;
}

export interface ColumnDefinition<T> {
  id?: string;
  field?: string;
  _valueGetter?: (row: T) => unknown;
  [key: string]: unknown;
}

// ============================================================================
// NULL HANDLING STRATEGY
// ============================================================================

/**
 * Determines how null/undefined values should be sorted.
 * - "first": Nulls appear before all other values
 * - "last": Nulls appear after all other values (default)
 */
export type NullPosition = "first" | "last";

// ============================================================================
// VALUE TYPE DETECTION
// ============================================================================

/**
 * Detects the type of a value for proper comparison
 */
export type ValueType = "null" | "number" | "date" | "string";

export const detectValueType = (value: unknown): ValueType => {
  if (value === null || value === undefined || value === "") {
    return "null";
  }

  // Check for NaN first (typeof NaN === "number" but it's invalid)
  if (typeof value === "number" && Number.isNaN(value)) {
    return "null";
  }

  // Check if it's a date (dayjs object or Date object)
  if (dayjs.isDayjs(value) || value instanceof Date) {
    return "date";
  }

  // Check if it's a number (not NaN)
  if (typeof value === "number") {
    return "number";
  }

  // Check if it's a string that can be parsed as a number
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed !== "" && !Number.isNaN(Number(trimmed))) {
      return "number";
    }
  }

  return "string";
};

// ============================================================================
// COMPARISON FUNCTIONS
// ============================================================================

/**
 * Compare two values handling null/undefined properly
 * Returns: -1 if a < b, 1 if a > b, 0 if equal
 */
export const compareValues = (
  valA: unknown,
  valB: unknown,
  sortAsc: boolean,
  nullPosition: NullPosition = "last",
): number => {
  const typeA = detectValueType(valA);
  const typeB = detectValueType(valB);

  // Handle null values
  const isNullA = typeA === "null";
  const isNullB = typeB === "null";

  if (isNullA && isNullB) return 0;
  if (isNullA) {
    // Null should go to the specified position regardless of sort direction
    return nullPosition === "last" ? 1 : -1;
  }
  if (isNullB) {
    return nullPosition === "last" ? -1 : 1;
  }

  // Both values are non-null, compare based on type
  let comparison = 0;

  // If both are numbers, compare numerically
  if (typeA === "number" && typeB === "number") {
    const numA = typeof valA === "number" ? valA : Number(valA);
    const numB = typeof valB === "number" ? valB : Number(valB);
    comparison = numA - numB;
  }
  // If both are dates, compare as dates
  else if (typeA === "date" && typeB === "date") {
    const dateA = dayjs.isDayjs(valA) ? valA : dayjs(valA as Date | number);
    const dateB = dayjs.isDayjs(valB) ? valB : dayjs(valB as Date | number);
    comparison = dateA.valueOf() - dateB.valueOf();
  }
  // Otherwise, compare as strings (case-insensitive)
  else {
    const strA = String(valA).toLowerCase();
    const strB = String(valB).toLowerCase();
    comparison = strA.localeCompare(strB);
  }

  // Handle -0 edge case (Object.is(-0, 0) is false)
  if (comparison === 0) return 0;

  // Apply sort direction
  return sortAsc ? comparison : -comparison;
};

/**
 * Compare two numbers with proper null handling
 */
export const compareNumbers = (
  a: unknown,
  b: unknown,
  sortAsc: boolean,
  nullPosition: NullPosition = "last",
): number => {
  const numA = Number(a);
  const numB = Number(b);
  const isNullA = a === null || a === undefined || a === "" || Number.isNaN(numA);
  const isNullB = b === null || b === undefined || b === "" || Number.isNaN(numB);

  if (isNullA && isNullB) return 0;
  if (isNullA) return nullPosition === "last" ? 1 : -1;
  if (isNullB) return nullPosition === "last" ? -1 : 1;

  const comparison = numA - numB;
  return sortAsc ? comparison : -comparison;
};

/**
 * Compare two strings with proper null handling (case-insensitive)
 */
export const compareStrings = (
  a: unknown,
  b: unknown,
  sortAsc: boolean,
  nullPosition: NullPosition = "last",
): number => {
  const isNullA = a === null || a === undefined;
  const isNullB = b === null || b === undefined;

  if (isNullA && isNullB) return 0;
  if (isNullA) return nullPosition === "last" ? 1 : -1;
  if (isNullB) return nullPosition === "last" ? -1 : 1;

  const strA = String(a).toLowerCase();
  const strB = String(b).toLowerCase();
  const comparison = strA.localeCompare(strB);
  return sortAsc ? comparison : -comparison;
};

/**
 * Compare two dates with proper null handling
 */
export const compareDates = (a: unknown, b: unknown, sortAsc: boolean, nullPosition: NullPosition = "last"): number => {
  const dateA = dayjs.isDayjs(a) ? a : dayjs(a as Date | number | string);
  const dateB = dayjs.isDayjs(b) ? b : dayjs(b as Date | number | string);

  const isValidA = dateA.isValid();
  const isValidB = dateB.isValid();

  // Also check for null/undefined/empty original values
  const isNullA = a === null || a === undefined || a === "" || !isValidA;
  const isNullB = b === null || b === undefined || b === "" || !isValidB;

  if (isNullA && isNullB) return 0;
  if (isNullA) return nullPosition === "last" ? 1 : -1;
  if (isNullB) return nullPosition === "last" ? -1 : 1;

  const comparison = dateA.valueOf() - dateB.valueOf();
  return sortAsc ? comparison : -comparison;
};

// ============================================================================
// MAIN SORT FUNCTION
// ============================================================================

/**
 * Sort a dataset by multiple conditions (multi-level sorting)
 * Conditions are applied in order - first condition is primary sort, second is secondary, etc.
 */
export const sortDataset = <T extends Record<string, unknown>>(
  data: T[],
  sortConditions: SortCondition[],
  columnDefinitions: ColumnDefinition<T>[],
  nullPosition: NullPosition = "last",
): T[] => {
  if (!sortConditions || sortConditions.length === 0) {
    return data;
  }

  return [...data].sort((a, b) => {
    for (const condition of sortConditions) {
      const columnId = condition.column;
      const sortAsc = condition.direction === "asc";

      // Find column definition
      const columnDef = columnDefinitions.find((col) => col.id === columnId || col.field === columnId);

      // Get values using valueGetter if available, otherwise use field directly
      const valA = columnDef?._valueGetter
        ? columnDef._valueGetter(a)
        : columnDef?.field
          ? a[columnDef.field as keyof T]
          : a[columnId as keyof T];

      const valB = columnDef?._valueGetter
        ? columnDef._valueGetter(b)
        : columnDef?.field
          ? b[columnDef.field as keyof T]
          : b[columnId as keyof T];

      const comparison = compareValues(valA, valB, sortAsc, nullPosition);

      // If values are different, return the comparison result
      if (comparison !== 0) {
        return comparison;
      }
      // If values are equal, continue to next sort condition
    }

    return 0;
  });
};

// ============================================================================
// SPECIALIZED SORT FUNCTIONS
// ============================================================================

/**
 * Sort by a single column (convenience function)
 */
export const sortByColumn = <T extends Record<string, unknown>>(
  data: T[],
  columnId: string,
  direction: SortDirection,
  columnDefinitions: ColumnDefinition<T>[],
  nullPosition: NullPosition = "last",
): T[] => {
  return sortDataset(data, [{ column: columnId, direction }], columnDefinitions, nullPosition);
};

/**
 * Sort numbers in a simple array
 */
export const sortNumbers = (
  data: number[],
  direction: SortDirection,
  nullPosition: NullPosition = "last",
): number[] => {
  const sortAsc = direction === "asc";
  return [...data].sort((a, b) => compareNumbers(a, b, sortAsc, nullPosition));
};

/**
 * Sort strings in a simple array (case-insensitive)
 */
export const sortStrings = (
  data: string[],
  direction: SortDirection,
  nullPosition: NullPosition = "last",
): string[] => {
  const sortAsc = direction === "asc";
  return [...data].sort((a, b) => compareStrings(a, b, sortAsc, nullPosition));
};

/**
 * Sort dates in a simple array
 */
export const sortDates = (
  data: (Date | number | string | null)[],
  direction: SortDirection,
  nullPosition: NullPosition = "last",
): (Date | number | string | null)[] => {
  const sortAsc = direction === "asc";
  return [...data].sort((a, b) => compareDates(a, b, sortAsc, nullPosition));
};

// ============================================================================
// STABILITY CHECK
// ============================================================================

/**
 * Check if an array is sorted according to the given conditions
 */
export const isSorted = <T extends Record<string, unknown>>(
  data: T[],
  sortConditions: SortCondition[],
  columnDefinitions: ColumnDefinition<T>[],
  nullPosition: NullPosition = "last",
): boolean => {
  if (data.length <= 1) return true;

  for (let i = 0; i < data.length - 1; i++) {
    const a = data[i]!;
    const b = data[i + 1]!;

    for (const condition of sortConditions) {
      const columnId = condition.column;
      const sortAsc = condition.direction === "asc";

      const columnDef = columnDefinitions.find((col) => col.id === columnId || col.field === columnId);

      const valA = columnDef?._valueGetter
        ? columnDef._valueGetter(a)
        : columnDef?.field
          ? a[columnDef.field as keyof T]
          : a[columnId as keyof T];

      const valB = columnDef?._valueGetter
        ? columnDef._valueGetter(b)
        : columnDef?.field
          ? b[columnDef.field as keyof T]
          : b[columnId as keyof T];

      const comparison = compareValues(valA, valB, sortAsc, nullPosition);

      if (comparison > 0) {
        // a should come after b, but it comes before - not sorted
        return false;
      }
      if (comparison < 0) {
        // a correctly comes before b, move to next pair
        break;
      }
      // comparison === 0, check next sort condition
    }
  }

  return true;
};
